import "./_DetailOperationCommerciale.scss";

import {
  BaseSyntheticEvent,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import {useNavigate} from "react-router-dom";

import {postFile} from "@/api/file";
import {fetchMagasin} from "@/api/magasin";
import {
  PostCommandeOperationCommerciale,
  PutCommandeOperationCommerciale,
} from "@/api/operationCommerciale";
import iconCross from "@/assets/massification/iconCross.svg";
import iconSave from "@/assets/massification/iconSave.svg";
import iconShop from "@/assets/massification/iconShop.svg";
import Button from "@/components/Button/Button";
import Loader from "@/components/Loader/loader";
import {Modale} from "@/components/Modale/Modale";
import Title from "@/components/Section/Title";
import SelectCustom from "@/components/SelectCustom/SelectCustom";
import {HookFormContext} from "@/context/HookFormContext";
import {formatDate} from "@/function/formatDate";
import {createCommande} from "@/function/operationCommercialeToole";
import {useRefuseOperation} from "@/hooks/useRefuseOperation";
import {OptionNameValue} from "@/ts/interface/formulaire";
import {Magasin} from "@/ts/interface/magasin";
import {
  FormOperationsCommercialesMap,
  OperationCommerciale,
} from "@/ts/interface/operationCommerciale";

import {FileAndLinkToDL} from "../FileAndLinkToDL/FileAndLinkToDL";
import OffreOperationCommerciale from "../OffreOperationCommerciale/OffreOperationCommerciale";
import {RecapCommande} from "../RecapCommande/RecapCommande";

function DetailOperationCommerciale({
  operation,
  hasSaveCommande = false,
  hasCommande = false,
  hasRefuse = false,
}: {
  operation: OperationCommerciale | null | undefined;
  hasSaveCommande: boolean;
  hasCommande: boolean;
  hasRefuse: boolean;
}): ReactElement {
  const navigate = useNavigate();
  const context = useContext(HookFormContext);

  if (!context) {
    throw new Error("HookFormContext is not provided.");
  }

  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    watch,
    formState: {errors},
    printError,
  } = context;
  useEffect(() => {
    console.log("valeur par defaut", getValues());
  }, []);
  const [magasins, setMagasins] = useState<Magasin[]>([]);
  const [magasinToSelect, setMagasinToSelect] = useState<OptionNameValue[]>([]);
  const {handleClickRefuse, modaleInfo, setModaleinfo} = useRefuseOperation(
    () => {
      window.location.reload();
    },
  );

  const isLivraisonUnique = watch("has_livraison_unique");

  useEffect(() => {
    const getMagasins = async (): Promise<void> => {
      const allMagasins = await fetchMagasin(
        "operation",
        operation?.operation_id.toString(),
      );
      setMagasins(allMagasins ? allMagasins : []);
    };
    getMagasins();
  }, []);
  useEffect(() => {
    const newMagasinToSelect = magasins.map(magasin => {
      return {
        libelle: magasin.raison_sociale,
        value: magasin.magasin_id || "",
        defaultChecked: false,
      };
    });
    setMagasinToSelect(newMagasinToSelect);
  }, [magasins]);
  useEffect(() => {
    if (isLivraisonUnique === "false") {
      setValue("magasin_de_livraison", undefined);
    }
  }, [isLivraisonUnique]);

  const handleRefuseOperation = async (): Promise<void> => {
    if (operation?.operation_id) {
      await handleClickRefuse(operation.operation_id);
    }
  };
  const onSubmit = async (
    data: FormOperationsCommercialesMap,
    e: BaseSyntheticEvent<object, any, any> | undefined,
  ): Promise<void> => {
    if (operation) {
      const submitEvent = e?.nativeEvent as SubmitEvent;
      const submitter = submitEvent.submitter as HTMLButtonElement;
      const action = submitter.value as "Enregistrer" | "Annuler" | "Validée";

      setModaleinfo({
        ...modaleInfo,
        isOpen: true,
        message: <Loader />,
        title: "Envoi en cours",
        variant: "primary",
      }); // Ouvre la modale
      const sendFichiers = async (): Promise<void> => {
        try {
          const uploadPromises: Promise<void>[] = [];

          Object.entries(data.offres)?.forEach(
            ([offreIndex, {magasins = {}}]) => {
              Object.entries(magasins)?.forEach(([magIndex, magasin]) => {
                if (!magasin?.items) return;

                Object.entries(magasin.items)?.forEach(
                  ([itemIndex, {livraisons}]) => {
                    if (!livraisons) return;

                    Object.entries(livraisons).forEach(
                      ([livraisonIndex, livraison]) => {
                        livraison?.fichiers?.forEach((fichier, index) => {
                          if (
                            Array.isArray(fichier.fichier) &&
                            fichier.fichier.length > 0 &&
                            !fichier.fichierId
                          ) {
                            const uploadPromise = (async () => {
                              const formDataToSend = new FormData();
                              formDataToSend.append("file", fichier.fichier[0]);

                              const response = await postFile(formDataToSend);
                              setValue(
                                `offres.${offreIndex}.magasins.${magIndex}.items.${itemIndex}.livraisons.${livraisonIndex}.fichiers[${index}].fichierId`,
                                response.fichierId,
                              );
                            })();

                            uploadPromises.push(uploadPromise);
                          } else {
                            setValue(
                              `offres.${offreIndex}.magasins.${magIndex}.items.${itemIndex}.livraisons.${livraisonIndex}.fichiers[${index}].fichierId`,
                              fichier.fichierId,
                            );
                          }
                        });
                      },
                    );
                  },
                );
              });
            },
          );

          await Promise.all(uploadPromises); // <-- attend tous les uploads
        } catch (error) {
          throw new Error(`Erreur lors de l'upload des fichiers : ${error}`);
        }
      };
      try {
        await sendFichiers(); // Envoie les fichiers avant de créer la commande
      } catch (error: any) {
        setModaleinfo({
          ...modaleInfo,
          isOpen: true,
          message: <p>Erreur lors de l'envoi des fichier</p>,
          title: `Echec de l'envoi des fichiers : ${error?.message}`,
          variant: "negate",
        }); // Ouvre la modale
        throw new Error(error?.message);
      }
      const updatedData = getValues();
      const commande = createCommande(
        updatedData,
        operation.operation_id,
        action,
      );
      try {
        if (!commande.ligne_commande?.length) {
          throw new Error("Aucun article n'a été commandé.");
        }
        if (hasSaveCommande && data?.commande_id) {
          await PutCommandeOperationCommerciale(
            commande,
            data.commande_id.toString(),
          );
        } else {
          await PostCommandeOperationCommerciale(commande);
        }
        // Si la réponse est réussie
        setModaleinfo({
          ...modaleInfo,
          isOpen: true,
          close: () => {
            if (action === "Validée") {
              navigate("/");
            } else {
              window.location.reload();
            }
          },
          message:
            action === "Validée" ? (
              <>
                <p>
                  Votre commande a bien été prise en compte. Vous pourrez la
                  retrouver dans votre espace Mon compte, rubrique Historique.
                </p>
                {/* <RecapCommande magasins={magasins} /> */}
              </>
            ) : (
              <>
                <p>
                  Votre commande a bien été enregistrée. Vous pouvez la modifier
                  à tout moment avant de la commander, en revenant sur cette
                  page.
                </p>
                {/* <RecapCommande magasins={magasins} /> */}
              </>
            ),
          title:
            action === "Validée"
              ? "Commande envoyée"
              : "Enregistrement de la commande",
          variant: "validate",
        }); // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        // En cas d'erreur
        setModaleinfo({
          ...modaleInfo,
          isOpen: true,
          message: (
            <p>
              <span>{`${err}`}</span>
              <br />
              Nous n’avons pas pu enregistrer votre commande. Merci de
              réessayer.
            </p>
          ),
          variant: "negate",
          title:
            action === "Validée"
              ? "Echec lors de l'envoi de la commande"
              : "Echec lors de l'enregistrement de la commande",
        });
      }
    }
  };
  const watcher = watch();

  useEffect(() => {
    console.log(watcher);
  }, [watcher]);
  if (operation) {
    return (
      <>
        <form
          className="operationCommerciale"
          onSubmit={handleSubmit(onSubmit)}>
          <Title
            title={operation.nom}
            complement={`Engagement du ${formatDate(operation?.date_engagement_debut)} au ${formatDate(operation?.date_engagement_fin)}`}
          />
          <div className="operationCommerciale-header">
            <div className="operationCommerciale-header--image">
              {operation.images?.[0]?.url ? (
                <img
                  src={operation.images?.[0]?.url}
                  alt={`illustration de l'operation ${operation.nom}`}
                />
              ) : null}
              <p>{`Parution du ${formatDate(operation?.date_validite_debut)} au ${formatDate(operation?.date_validite_fin)}`}</p>
              {operation.obligatoire && (
                <div className="operationCommerciale-obligatoire">
                  <p>Obligatoire</p>
                </div>
              )}
            </div>
            <div
              className="operationCommerciale-header--description description-text"
              dangerouslySetInnerHTML={{__html: operation?.description}}></div>
            <div className="operationCommerciale-header--button">
              <div className="operationCommerciale-header--button">
                {hasRefuse ? (
                  <p className="operationRefuse">
                    Vous avez refusé cette offre
                  </p>
                ) : (
                  <Button
                    label={
                      hasSaveCommande
                        ? "Continuer mon engagement"
                        : "Je m'engage"
                    }
                    onClick={() => {
                      window.location.href = "#parametrageOpe";
                    }}
                    className="operationCommerciale-header--buttonEngagement"
                    variant="secondary"
                  />
                )}
                {operation.obligatoire || hasCommande ? null : (
                  <Button
                    label="Refuser"
                    onClick={() => handleRefuseOperation()}
                    className="operationCommerciale-header--buttonNoEngagement"
                    variant="negate"
                    value="Refusée"
                  />
                )}
              </div>
              <div className="operationCommerciale-header--button">
                <FileAndLinkToDL
                  liens={operation.liens}
                  fichiers={operation.fichiers}
                />
              </div>
              <div></div>
            </div>
          </div>
          <Title title={"Parametrage de l'engagement"} id="parametrageOpe" />
          <div>
            {operation?.offres?.map((offre, index) => {
              return (
                <OffreOperationCommerciale
                  offre={offre}
                  magasins={magasins}
                  key={`${offre.nom} ${index}`}
                />
              );
            })}
          </div>
          <Title title={"Récapitulatif de la commande"} />
          <RecapCommande magasins={magasins} />
          <div className="operationCommerciale-footer--parametrage">
            <div className="operationCommerciale-footer--selection">
              <div className="operationCommerciale-footer--radio">
                <p>Grouper la livraison dans un unique point de vente ? </p>
                <div>
                  <input
                    type="radio"
                    id="oui"
                    {...register("has_livraison_unique")}
                    value="true"
                  />
                  <label htmlFor="oui">Oui</label>
                </div>
                <div>
                  <input
                    type="radio"
                    id="non"
                    {...register("has_livraison_unique")}
                    value="false"
                  />
                  <label htmlFor="non">Non</label>
                </div>
              </div>
              {isLivraisonUnique === "true" ? (
                <>
                  <SelectCustom
                    label="Magasin de livraison"
                    handleSelect={() => {}}
                    placeholder="-- Magasin livraison unique --"
                    defaultValue=""
                    options={magasinToSelect}
                    setValue={setValue}
                    watch={watch}
                    onTrash={() => {
                      setValue("magasin_de_livraison", undefined);
                    }}
                    {...register("magasin_de_livraison", {
                      required:
                        "Choisissez un magasin de livraison ou refusez la livraison unique",
                    })}
                  />
                  {printError("magasin_de_livraison.message", errors)}
                </>
              ) : null}
            </div>
            <div>
              <textarea
                id="commentaire"
                {...register("commentaire")}
                className="textarea"
                placeholder="Commentaire"
              />
            </div>
          </div>
          {hasRefuse ? (
            <p
              className="operationRefuse"
              style={{textAlign: "center", marginTop: "15px"}}>
              Vous avez refusé cette offre
            </p>
          ) : (
            <div className="operationCommerciale-footer--submitButton">
              <Button
                label="Annuler"
                variant="negate"
                type="button"
                icon={iconCross}
                onClick={() => {
                  navigate(-1);
                }}
              />
              <Button
                label="Enregistrer"
                value="Enregistrée"
                type="submit"
                variant="validate"
                icon={iconSave}
                onClick={() => {}}
              />
              <Button
                label="Commander"
                value="Validée"
                type="submit"
                variant="secondary"
                icon={iconShop}
                onClick={() => {
                  console.log(errors);
                }}
              />
            </div>
          )}
        </form>
        <Modale
          title={modaleInfo.title}
          isOpen={modaleInfo.isOpen}
          variant={modaleInfo.variant}
          close={modaleInfo.close}>
          {modaleInfo.message}
        </Modale>
      </>
    );
  } else {
    return (
      <>
        <Loader />
      </>
    );
  }
}

export default DetailOperationCommerciale;
